import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { SiteCustomizationProvider, useSiteCustomization } from "@/context/SiteCustomizationContext";

// Layout
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SeasonalDecorations from "@/components/SeasonalDecorations";
import ProtectedRoute from "@/components/ProtectedRoute";

// Pages
import HomePage from "@/pages/HomePage";
import HeroLandingPage from "@/pages/HeroLandingPage";
import MockExamsPage from "@/pages/MockExamsPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import CheckoutPage from "@/pages/CheckoutPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import PaymentFailurePage from "@/pages/PaymentFailurePage";
import MockPaymentPage from "@/pages/MockPaymentPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import DashboardPage from "@/pages/DashboardPage";
import SchoolServicesPage from "@/pages/SchoolServicesPage";
import AdminPage from "@/pages/AdminPage";
import AnalyticsDashboard from "@/pages/AnalyticsDashboard";
import SuccessStoriesPage from "@/pages/SuccessStoriesPage";
import FeaturedProductsPage from "@/pages/FeaturedProductsPage";
import SupportPage from "@/pages/SupportPage";
import AuthCallback from "@/pages/AuthCallback";
import MicrosoftAuthCallback from "@/pages/MicrosoftAuthCallback";
import RoleSelectionPage from "@/pages/RoleSelectionPage";
import AccessCodePage from "@/pages/AccessCodePage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import OrderHistoryPage from "@/pages/OrderHistoryPage";
import SamplePreviewPage from "@/pages/SamplePreviewPage";
import MyProgressPage from "@/pages/MyProgressPage";
import MyDevicesPage from "@/pages/MyDevicesPage";
import VerifyDevicePage from "@/pages/VerifyDevicePage";
import QuizChallengePage from "@/pages/QuizChallengePage";

import "@/App.css";

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// Show hero landing for unauthenticated users, real HomePage for authenticated
function HomeRoute() {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <HomePage /> : <HeroLandingPage />;
}

function AppContent() {
  const location = useLocation();
  const { user, loading } = useAuth();
  const isHomePage = location.pathname === "/";
  const isAuthCallback = location.pathname.startsWith("/auth/callback");
  const { isSectionEnabled } = useSiteCustomization();

  // Don't show header/footer on auth callback pages
  if (isAuthCallback) {
    return (
      <Routes>
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/auth/callback/microsoft" element={<MicrosoftAuthCallback />} />
      </Routes>
    );
  }

  // Role selection page also has its own layout (no header/footer)
  if (location.pathname === "/select-role") {
    return (
      <Routes>
        <Route path="/select-role" element={<RoleSelectionPage />} />
      </Routes>
    );
  }

  // Access code page has its own layout (no header/footer)
  if (location.pathname === "/admin/access-code") {
    return (
      <Routes>
        <Route path="/admin/access-code" element={<AccessCodePage />} />
      </Routes>
    );
  }

  // Hero landing page has its own full-screen layout (no header/footer)
  const showHeroLanding = isHomePage && !loading && !user;
  if (showHeroLanding) {
    return (
      <>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<HeroLandingPage />} />
        </Routes>
      </>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <ScrollToTop />
      <SeasonalDecorations />
      {isSectionEnabled("header") && <Header />}
      <main className={`flex-1 ${isHomePage ? '' : 'pt-20'}`}>
        <Routes>
          <Route path="/" element={<HomeRoute />} />
          {/* Public: browsing catalog */}
          <Route path="/mock-exams" element={<MockExamsPage />} />
          <Route path="/mock-exams/:slug" element={<ProductDetailPage />} />
          <Route path="/sample-preview" element={<SamplePreviewPage />} />
          <Route path="/sample-preview/:id" element={<SamplePreviewPage />} />
          <Route path="/my-progress" element={<MyProgressPage />} />
          <Route path="/my-devices" element={<ProtectedRoute><MyDevicesPage /></ProtectedRoute>} />
          <Route path="/verify-device" element={<VerifyDevicePage />} />
          <Route path="/quiz-challenge" element={<QuizChallengePage />} />
          <Route path="/school-services" element={<SchoolServicesPage />} />
          <Route path="/success-stories" element={<SuccessStoriesPage />} />
          <Route path="/featured-products" element={<FeaturedProductsPage />} />
          <Route path="/support/:pageKey" element={<SupportPage />} />
          {/* Auth pages */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/auth/callback/microsoft" element={<MicrosoftAuthCallback />} />
          <Route path="/select-role" element={<RoleSelectionPage />} />
          {/* Protected: require auth */}
          <Route path="/checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
          <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} />
          <Route path="/payment/failure" element={<ProtectedRoute><PaymentFailurePage /></ProtectedRoute>} />
          <Route path="/payment/mock" element={<ProtectedRoute><MockPaymentPage /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/orders" element={<ProtectedRoute><OrderHistoryPage /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
          <Route path="/admin/analytics" element={<ProtectedRoute><AnalyticsDashboard /></ProtectedRoute>} />
        </Routes>
      </main>
      {isSectionEnabled("footer") && <Footer />}
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <SiteCustomizationProvider>
            <AppContent />
            <Toaster position="top-right" />
          </SiteCustomizationProvider>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
